/**
 * Voice call panel (SD-211) — covers the chat body while a call is running.
 *
 * Display contract (design §6.2): ONE fixed agent bubble. Fragments stream
 * into it; a finished turn stays visible; the next turn's first fragment
 * replaces it (the reset flag is computed by the shared bridge). The user's
 * own speech is never displayed.
 */
import { useEffect, useRef, type MutableRefObject } from 'react';
import type { VoiceCallState } from '../voice/useVoiceCall';

type LevelSinkRef = MutableRefObject<((level: number) => void) | null>;

interface Props {
  state: VoiceCallState;
  levelSinkRef: LevelSinkRef;
  botName: string;
  t: (key: string) => string;
  onEnd: () => void;
  onToggleMute: () => void;
  onChip: (text: string) => void;
}

const WAVE_BARS = [0.55, 0.85, 1, 0.75, 0.5];

/**
 * The bars follow the agent's voice, which changes as fast as the animation
 * runs. Driving that through render state would re-render the whole panel —
 * caption, chips and all — on every frame, so the transforms are written
 * straight to the DOM instead.
 *
 * There is no loop here: the bridge already reads the level on its own rAF and
 * only calls back when the value changed, so painting inside the callback is
 * both sufficient and idle when the line is quiet. This registers the painter
 * and lets that loop drive it, the same way the widget consumer does.
 */
function CallWaveform({ levelSinkRef }: { levelSinkRef: LevelSinkRef }) {
  const barsRef = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    const paint = (level: number) => {
      for (let i = 0; i < WAVE_BARS.length; i++) {
        const bar = barsRef.current[i];
        if (!bar) continue;
        const scale = Math.max(0.15, Math.min(1, level * 3 * WAVE_BARS[i]));
        bar.style.transform = `scaleY(${scale})`;
      }
    };
    levelSinkRef.current = paint;
    paint(0);
    return () => {
      levelSinkRef.current = null;
    };
  }, [levelSinkRef]);

  return (
    <div className="call-waveform" aria-hidden="true">
      {WAVE_BARS.map((_weight, i) => (
        <span
          key={i}
          className="call-wave-bar"
          ref={(el) => {
            barsRef.current[i] = el;
          }}
        />
      ))}
    </div>
  );
}

export function CallPanel({
  state,
  levelSinkRef,
  botName,
  t,
  onEnd,
  onToggleMute,
  onChip,
}: Props) {
  const connecting = state.status === 'connecting';
  return (
    <div className="call-panel" role="dialog" aria-label={t('voice.a11y.panel')}>
      <div className="call-status">
        <div className="call-bot-name">{botName}</div>
        <div className="call-state-label">
          {connecting ? t('voice.connecting') : t('voice.live')}
        </div>
      </div>

      <CallWaveform levelSinkRef={levelSinkRef} />

      <div className="call-caption" aria-live="polite">
        {state.agentText && <div className="call-bubble">{state.agentText}</div>}
      </div>

      {state.candidates.length > 0 && (
        <div className="call-chips" role="group" aria-label={t('a11y.quickReply')}>
          {state.candidates.map((text) => (
            <button key={text} type="button" className="call-chip" onClick={() => onChip(text)}>
              {text}
            </button>
          ))}
        </div>
      )}

      {state.endingSoonSeconds !== null && (
        <div className="call-ending-soon">{t('voice.endingSoon')}</div>
      )}

      <div className="call-controls">
        <button
          type="button"
          className={`call-ctl mute ${state.muted ? 'active' : ''}`}
          onClick={onToggleMute}
          disabled={connecting}
          aria-pressed={state.muted}
          aria-label={state.muted ? t('voice.unmute') : t('voice.mute')}
        >
          <MicIcon off={state.muted} />
          <span>{state.muted ? t('voice.unmute') : t('voice.mute')}</span>
        </button>
        <button
          type="button"
          className="call-ctl end"
          onClick={onEnd}
          aria-label={t('voice.end')}
        >
          <EndIcon />
          <span>{t('voice.end')}</span>
        </button>
      </div>
    </div>
  );
}

export function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function MicIcon({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="22" />
      {off && <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}

function EndIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: 'rotate(135deg)' }}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}
